import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { MaskLine } from "../components/Motion";
import { OpenStatus } from "../components/OpenStatus";
import { waLink, WhatsAppIcon } from "../components/WhatsApp";
import { PlanningModule } from "../components/PlanningModule";
import { api, formatApiErrorDetail, fileUrl } from "../lib/api";
import { useContent } from "../context/ContentContext";

const DRIVE = "https://images.unsplash.com/photo-1610809589386-9ea41901eb54?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Contact() {
  const { get } = useContent();
  const [params] = useSearchParams();
  const autoId = params.get("auto");
  const [car, setCar] = useState(null);
  const [form, setForm] = useState({ naam: "", email: "", telefoon: "", bericht: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [waText, setWaText] = useState("");

  useEffect(() => {
    if (autoId) api.get(`/cars/${autoId}`).then((r) => setCar(r.data)).catch(() => {});
  }, [autoId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.naam || !form.email) { toast.error("Vul uw naam en e-mailadres in."); return; }
    setSending(true);
    try {
      await api.post("/requests", {
        ...form,
        type: autoId ? "proefrit" : "contact",
        auto_id: autoId || null,
        auto_titel: car ? `${car.merk} ${car.model}` : null,
      });
      setDone(true);
      toast.success("Bedankt! We nemen zo snel mogelijk contact met u op.");
      const lines = [
        `${autoId ? "Proefrit-aanvraag" : "Contactverzoek"} via de website:`,
        `Naam: ${form.naam}`,
        form.telefoon ? `Telefoon: ${form.telefoon}` : null,
        car ? `Auto: ${car.merk} ${car.model}` : null,
        form.bericht ? `Bericht: ${form.bericht}` : null,
      ].filter(Boolean);
      setWaText(lines.join("\n"));
      setForm({ naam: "", email: "", telefoon: "", bericht: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const inp = "w-full bg-surface border border-border px-4 py-3.5 outline-none focus:border-primary transition-colors placeholder:text-text-muted/60";

  return (
    <div className="pt-28 pb-0 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <p className="text-xs font-bold tracking-[0.25em] uppercase text-primary mb-4">
          {autoId ? get("contact.eyebrow_proefrit", "Proefrit aanvragen") : get("contact.eyebrow_contact", "Neem contact op")}
        </p>
        <h1 className="font-display font-black uppercase tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-none text-text-main">
          <MaskLine>{autoId ? get("contact.title_proefrit1", "Plan uw ") : get("contact.title_contact1", "Kom eens ")}</MaskLine>
          <MaskLine delay={0.12}><span className="text-primary">{autoId ? get("contact.title_proefrit2", "proefrit") : get("contact.title_contact2", "langs")}</span></MaskLine>
        </h1>
      </div>

      <div className="grid lg:grid-cols-2 mt-16 border-t border-border">
        {/* Left: info + image */}
        <div className="bg-primary text-white relative overflow-hidden grain">
          <img src={fileUrl(get("afbeeldingen.contact_drive", DRIVE))} alt="Rijden in de natuur" className="w-full h-64 lg:h-80 object-cover opacity-90" />
          <div className="p-8 lg:p-14 relative z-10 space-y-6">
            {car && (
              <div className="bg-white/10 border border-white/20 p-5 mb-2" data-testid="selected-car">
                <p className="text-xs uppercase tracking-widest text-accent font-bold">Geselecteerde auto</p>
                <p className="font-display font-bold text-xl mt-1">{car.merk} {car.model}</p>
              </div>
            )}
            <InfoRow icon={MapPin} title="Adres" lines={[get("bedrijf.adres_regel1", "Herenweg 165"), get("bedrijf.adres_regel2", "3645 DK Vinkeveen")]} />
            <InfoRow icon={Phone} title="Telefoon" lines={[get("bedrijf.telefoon", "0297 - 261 333")]} />
            <InfoRow icon={Mail} title="E-mail" lines={[get("bedrijf.email", "info@stoofvinkeveen.nl")]} />
            <InfoRow icon={Clock} title="Openingstijden" lines={[get("bedrijf.open_ma_vr", "Ma t/m vr: 07:00 – 18:00"), get("bedrijf.open_za", "Zaterdag: 08:00 – 18:00"), get("bedrijf.open_zo", "Zondag: gesloten")]} />
            <OpenStatus variant="dark" />
          </div>
        </div>

        {/* Right: form */}
        <div className="p-8 lg:p-14 bg-background">
          {done ? (
            <div className="h-full flex flex-col justify-center py-16" data-testid="contact-success">
              <h2 className="font-display font-bold text-3xl text-primary">{get("contact.success_title", "Bedankt!")}</h2>
              <p className="text-text-muted mt-4 max-w-md">{get("contact.success_text", "Uw aanvraag is ontvangen. We nemen zo snel mogelijk contact met u op om alles verder te regelen.")}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={waLink(waText || "Hallo Automobielbedrijf Stoof, ik heb zojuist een aanvraag ingevuld.")}
                  target="_blank" rel="noreferrer" data-testid="wa-confirm-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white font-semibold px-6 py-3 hover:brightness-95 transition-all"
                >
                  <WhatsAppIcon size={18} /> Verstuur ook via WhatsApp
                </a>
                <button onClick={() => setDone(false)} className="rounded-full border border-text-main/20 font-semibold px-6 py-3 hover:border-primary hover:text-primary transition-colors">
                  Nog een bericht sturen
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5 max-w-lg" data-testid="contact-form">
              <div>
                <label className="text-sm font-semibold text-text-main">Naam *</label>
                <input data-testid="input-naam" className={inp + " mt-2"} value={form.naam} onChange={(e) => setForm({ ...form, naam: e.target.value })} placeholder="Uw naam" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-sm font-semibold text-text-main">E-mail *</label>
                  <input data-testid="input-email" type="email" className={inp + " mt-2"} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="naam@email.nl" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-text-main">Telefoon</label>
                  <input data-testid="input-telefoon" className={inp + " mt-2"} value={form.telefoon} onChange={(e) => setForm({ ...form, telefoon: e.target.value })} placeholder="06 - ..." />
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-text-main">Bericht</label>
                <textarea data-testid="input-bericht" rows={5} className={inp + " mt-2 resize-none"} value={form.bericht} onChange={(e) => setForm({ ...form, bericht: e.target.value })} placeholder={autoId ? "Wanneer zou u langs willen komen?" : "Waarmee kunnen we u helpen?"} />
              </div>
              <button type="submit" disabled={sending} data-testid="submit-contact"
                className="w-full rounded-full bg-primary text-white font-semibold px-7 py-4 hover:bg-primary-light transition-colors disabled:opacity-60">
                {sending ? "Versturen..." : autoId ? "Proefrit aanvragen" : "Bericht versturen"}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* MAP */}
      <section data-testid="map-section" className="mt-0">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-6">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-primary">{get("contact.map_eyebrow", "Route")}</p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mt-3">{get("contact.map_title", "U vindt ons hier")}</h2>
          <p className="text-text-muted mt-3">{get("contact.map_text", "Herenweg 165, 3645 DK Vinkeveen — ruime parkeergelegenheid voor de deur.")}</p>
          <a
            href="https://www.google.com/maps/dir/?api=1&destination=Herenweg+165+3645+DK+Vinkeveen"
            target="_blank" rel="noreferrer" data-testid="route-link"
            className="mt-5 group inline-flex items-center gap-2 rounded-full bg-primary text-white font-semibold px-6 py-3 hover:bg-primary-light transition-colors"
          >
            {get("contact.map_btn", "Plan uw route")}
            <span className="transition-transform group-hover:translate-x-1">→</span>
          </a>
        </div>
        <div className="w-full h-[420px] border-t border-border">
          <iframe
            title="Route naar Automobielbedrijf Stoof"
            src="https://www.google.com/maps?q=Herenweg%20165%203645%20DK%20Vinkeveen&output=embed"
            className="w-full h-full grayscale-[0.15]"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
      </section>

      {/* ONLINE AFSPRAAK */}
      <section data-testid="planning-section" className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 pb-8">
        <p className="text-xs font-bold tracking-[0.25em] uppercase text-primary">{get("contact.planning_eyebrow", "Werkplaats")}</p>
        <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mt-3">{get("contact.planning_title", "Direct online een afspraak maken")}</h2>
        <p className="text-text-muted mt-3 max-w-xl">{get("contact.planning_text", "Plan zelf een moment voor onderhoud, APK of reparatie — snel en zonder bellen.")}</p>
        <div className="mt-10 bg-surface border border-border p-4 sm:p-8">
          <PlanningModule />
        </div>
      </section>
    </div>
  );
}

const InfoRow = ({ icon: Icon, title, lines }) => (
  <div className="flex gap-4">
    <Icon size={20} className="text-accent mt-1 shrink-0" />
    <div>
      <p className="text-xs uppercase tracking-widest text-accent font-bold">{title}</p>
      {lines.map((l) => <p key={l} className="text-white/85">{l}</p>)}
    </div>
  </div>
);
